import React, { useRef, useLayoutEffect, useState } from 'react';
import { Army, ArmyConfig } from '../armyConfig';
import NumberPillInput from './NumberPillInput';
import { Player } from '../App';
import ArmyComboBox from './ArmyComboBox';

interface ScorePanelProps {
  attackerArmy: Army | undefined;
  defenderArmy: Army | undefined;
  armies: Record<Army, ArmyConfig>;
  scores: { attacker: number[]; defender: number[] };
  onArmyChange: (player: Player, army: Army) => void;
  onScoreChange: (player: Player, round: number, value: number) => void;
  totalAttacker: number;
  totalDefender: number;
  onCopy: () => void;
  onClear: () => void;
  copyStatus: string;
  rounds: number;
  currentRound: number;
  setCurrentRound: (round: number) => void;
}

const ScorePanel: React.FC<ScorePanelProps> = ({
  attackerArmy,
  defenderArmy,
  armies,
  scores,
  onArmyChange,
  onScoreChange,
  totalAttacker,
  totalDefender,
  onCopy,
  onClear,
  copyStatus,
  rounds,
  currentRound,
  setCurrentRound,
}) => {
  const roundRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevRoundRef = useRef(currentRound);
  const [indicatorPosition, setIndicatorPosition] = useState({ top: 0, height: 0 });
  const [controlsPosition, setControlsPosition] = useState({ top: 0 });
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [, forceRender] = useState({});
  const [screenMode, setScreenMode] = useState<'mobile' | 'wide' | 'ultrawide'>(() => {
    if (typeof window === 'undefined') return 'mobile';
    if (window.innerWidth >= 1200) return 'ultrawide';
    if (window.innerWidth >= 900) return 'wide';
    return 'mobile';
  });

  // Check if round is changing
  const isRoundChanging = prevRoundRef.current !== currentRound;
  if (isRoundChanging) {
    prevRoundRef.current = currentRound;
  }

  // Update positions when currentRound changes or component mounts
  useLayoutEffect(() => {
    // Set transitioning state immediately and force re-render
    setIsTransitioning(true);
    forceRender({});
    
    const updatePositions = () => {
      const currentRoundElement = roundRefs.current[currentRound];
      const container = containerRef.current;
      
      if (currentRoundElement && container) {
        const containerRect = container.getBoundingClientRect();
        const roundRect = currentRoundElement.getBoundingClientRect();
        
        // Calculate position relative to container
        const relativeTop = roundRect.top - containerRect.top;
        const rowHeight = roundRect.height;
        const indicatorHeight = 48; // Content height
        const indicatorBorder = 4; // 2px border on top and bottom
        const totalIndicatorHeight = indicatorHeight + indicatorBorder;
        
        // Center the indicator within the row: rowTop + (rowHeight - totalIndicatorHeight)/2
        const indicatorTop = relativeTop + (rowHeight - totalIndicatorHeight) / 2;
        
        setIndicatorPosition({ top: indicatorTop, height: indicatorHeight });
        setControlsPosition({ top: relativeTop + rowHeight / 2 });
      }
    };

    // Update positions after a brief delay to ensure DOM is ready
    const updateTimeout = setTimeout(updatePositions, 0);
    
    // Clear transitioning state after animation completes (300ms)
    const transitionTimeout = setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
    
    // Also update on resize in case layout changes
    window.addEventListener('resize', updatePositions);
    return () => {
      window.removeEventListener('resize', updatePositions);
      clearTimeout(updateTimeout);
      clearTimeout(transitionTimeout);
    };
  }, [currentRound, rounds]);

  React.useEffect(() => {
    const handler = () => {
      if (window.innerWidth >= 1200) {
        setScreenMode('ultrawide');
      } else if (window.innerWidth >= 900) {
        setScreenMode('wide');
      } else {
        setScreenMode('mobile');
      }
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const mobilePresets = [0, 3, 6];
  const widePresets = [0, 2, 4, 6];
  const ultrawidePresets = [0, 1, 2, 3, 4, 5, 6];
  let presetValues = mobilePresets;
  if (screenMode === 'ultrawide') presetValues = ultrawidePresets;
  else if (screenMode === 'wide') presetValues = widePresets;

  const isWide = screenMode === 'wide' || screenMode === 'ultrawide';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      {/* Army Selection */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: isWide ? 0 : 16 }}>
        <ArmyComboBox
          value={attackerArmy}
          onChange={(value) => onArmyChange('Attacker', value)}
          placeholder="Select attacker army..."
          align="left"
        />
        <ArmyComboBox
          value={defenderArmy}
          onChange={(value) => onArmyChange('Defender', value)}
          placeholder="Select defender army..."
          align="right"
        />
      </div>
      <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
        {/* Floating Round Indicator */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: indicatorPosition.top,
            height: indicatorPosition.height,
            background: 'rgba(0,255,0,0.08)',
            border: '2px solid #4caf50',
            borderRadius: 8,
            boxShadow: '0 2px 8px 0 rgba(76,175,80,0.10)',
            transition: 'top 0.3s ease-out, height 0.3s ease-out',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
        {/* Round Navigation Controls */}
        <div style={{
          position: 'absolute',
          right: 4,
          top: controlsPosition.top,
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 10,
          transition: 'top 0.3s ease-out',
        }}>
          <button
            onClick={() => setCurrentRound(Math.max(0, currentRound - 1))}
            disabled={currentRound === 0}
            style={{
              background: 'none',
              border: 'none',
              color: currentRound === 0 ? '#666' : '#4caf50',
              fontSize: 22,
              cursor: currentRound === 0 ? 'not-allowed' : 'pointer',
              marginBottom: 2,
              transition: 'color 0.2s',
              padding: 0,
              lineHeight: 1,
            }}
            aria-label="Previous round"
          >
            ▲
          </button>
          <button
            onClick={() => setCurrentRound(Math.min(rounds - 1, currentRound + 1))}
            disabled={currentRound === rounds - 1}
            style={{
              background: 'none',
              border: 'none',
              color: currentRound === rounds - 1 ? '#666' : '#4caf50',
              fontSize: 22,
              cursor: currentRound === rounds - 1 ? 'not-allowed' : 'pointer',
              marginTop: 2,
              transition: 'color 0.2s',
              padding: 0,
              lineHeight: 1,
            }}
            aria-label="Next round"
          >
            ▼
          </button>
        </div>
        {[...Array(rounds)].map((_, i) => {
          // Calculate cumulative totals
          const attackerCumulative = scores.attacker.slice(0, i + 1).reduce((sum, score) => sum + (score || 0), 0);
          const defenderCumulative = scores.defender.slice(0, i + 1).reduce((sum, score) => sum + (score || 0), 0);
          
          return (
            <div
              key={i}
              ref={(el) => { roundRefs.current[i] = el; }}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginBottom: 8,
                width: '100%',
                alignItems: 'center',
                borderRadius: 8,
                border: '2px solid transparent',
                minHeight: 48,
                position: 'relative',
                zIndex: 2,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <NumberPillInput
                  value={scores.attacker[i]}
                  onChange={v => onScoreChange('Attacker', i, v)}
                  min={0}
                  ariaLabel={`Attacker round ${i + 1} score`}
                  disabled={i !== currentRound || isTransitioning || isRoundChanging}
                  presetValues={presetValues}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <NumberPillInput
                  value={scores.defender[i]}
                  onChange={v => onScoreChange('Defender', i, v)}
                  min={0}
                  ariaLabel={`Defender round ${i + 1} score`}
                  disabled={i !== currentRound || isTransitioning || isRoundChanging}
                  presetValues={presetValues}
                />
              </div>
              {/* Cumulative totals - positioned between the pills */}
              {(scores.attacker[i] !== undefined && scores.attacker[i] !== null && scores.defender[i] !== undefined && scores.defender[i] !== null) && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  gap: 8,
                  zIndex: 1,
                  pointerEvents: 'none',
                }}>
                  <div style={{
                    fontSize: 12,
                    color: '#666',
                    opacity: 0.7,
                    fontWeight: 'normal',
                    textAlign: 'center',
                    minWidth: 20,
                  }}>
                    {attackerCumulative}
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: '#666',
                    opacity: 0.7,
                    fontWeight: 'normal',
                    textAlign: 'center',
                    minWidth: 8,
                  }}>
                    -
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: '#666',
                    opacity: 0.7,
                    fontWeight: 'normal',
                    textAlign: 'center',
                    minWidth: 20,
                  }}>
                    {defenderCumulative}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '16px 0 0 0', fontWeight: 'bold', fontSize: 18, width: '100%' }}>
        <div style={{ textAlign: 'center', background: '#333', borderRadius: 6, padding: 8, boxSizing: 'border-box' }}>Total: {totalAttacker}</div>
        <div style={{ textAlign: 'center', background: '#333', borderRadius: 6, padding: 8, boxSizing: 'border-box' }}>Total: {totalDefender}</div>
      </div>
      <div style={{ maxWidth: 420, margin: '32px auto 0 auto', width: '100%', display: 'flex', flexDirection: 'row', gap: 12 }}>
        <button
          onClick={onCopy}
          style={{ background: '#61dafb', color: '#222', padding: '14px 0', border: 'none', borderRadius: 6, fontWeight: 'bold', fontSize: 20, cursor: 'pointer', width: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <span className="material-icons" style={{ fontSize: 24, verticalAlign: 'middle' }}>content_copy</span>
          <span>{copyStatus ? copyStatus : 'Copy'}</span>
        </button>
        <button
          onClick={onClear}
          style={{ background: '#e74c3c', color: '#fff', padding: '14px 0', border: 'none', borderRadius: 6, fontWeight: 'bold', fontSize: 20, cursor: 'pointer', width: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <span className="material-icons" style={{ fontSize: 24, verticalAlign: 'middle' }}>delete</span>
          <span>Clear</span>
        </button>
      </div>
    </div>
  );
};

export default ScorePanel; 